import cloneDeep from 'lodash/cloneDeep';
import isObject from '@/utils/is-object';
import mergeDeep from '@/utils/merge-deep';
import omit from '@/utils/omit';

import { initialColors } from './colors';
import { initialFonts } from './fonts';
import { Theme } from './typings';
import { initialTypographies } from './typographies';
import { initialVariants } from './variants';
import { getValueFromProperties, getValueFromKeypath } from './index';

/**
 * Get a theme which only contains the default, minimal design tokens
 */
export const getInitialTheme = (): Theme => ({
	tokens: {
		fonts: initialFonts,
		typographies: initialTypographies,
		colors: initialColors,
		variants: initialVariants,
	},
	blocks: {},
	templates: {},
});

/**
 * Merge the given theme with the initial theme
 * Useful when you want to ensure that all minimal/required design tokens exist
 */
export const getThemeWithFallbacks = (theme: Theme) =>
	mergeDeep(getInitialTheme(), theme);

/**
 * Get the decoded theme that contains the value to be used by css-in-js (theme.json values and default attributes merged together)
 * @param rawTheme [object] The raw theme
 * @param blocksThemeControls [array] The array of blocks theme controls (taken from theming-loader.ts)
 * @returns [object] decodedTheme
 */
export const getDecodedTheme = (
	rawTheme,
	blocksThemeControls,
	templatesThemeControls
) => {
	if (
		(!blocksThemeControls || !blocksThemeControls.length) &&
		(!templatesThemeControls || !templatesThemeControls.length)
	)
		return rawTheme;

	// Deepclone
	const blocks = cloneDeep(blocksThemeControls);
	const templates = cloneDeep(templatesThemeControls);
	const decodedTheme = { blocks: {}, templates: {} };

	blocks.map((block) => {
		// Loop through each variant
		const { default: blockDefault, ...blockVariants } = Object.keys(
			rawTheme.tokens.variants
		).reduce(
			(acc, variant) => {
				// Get the value needed for css-in-js for each property
				const variantValue = getDecodedObject({
					object: omit(block.attributes, ['states']),
					rawTheme,
					blockSlug: block.slug,
					variant,
					state: 'default',
					blockParentType: 'blocks',
				});

				acc[variant] = variantValue;

				// Get the values for each states
				if (block.attributes['states']) {
					const statesValue = Object.keys(
						block.attributes['states']
					).reduce((acc, state) => {
						// Clear default attributes values
						// (to only have default values for the ones defined in the current state attributes - but still loop through every property to check if there are some values saved in the theme.json)
						// (Note: Empty attributes allows to avoid useless css for css-in-js)
						const defaultAttrs = cloneDeep(
							omit(block.attributes, ['states'])
						);
						clearObject(defaultAttrs);

						// Get the value needed for css-in-js for each property
						const stateValue = getDecodedObject({
							object: mergeDeep(
								defaultAttrs,
								block.attributes['states']?.[state]
							),
							rawTheme,
							blockSlug: block.slug,
							variant,
							state,
							blockParentType: 'blocks',
						});

						acc[state] = stateValue;

						return acc;
					}, {});

					acc[variant].states = statesValue;
				}

				return acc;
			},
			{ default: {} }
		);

		// Set those values to the current block in the fullRawTheme
		decodedTheme.blocks[block.slug] = {
			...blockDefault,
			variants: { ...blockVariants },
		};
	});

	templates.map((template) => {
		// Get the values (default, origin, value) for each property
		const templateValue = getDecodedObject({
			object: template.attributes,
			rawTheme,
			blockSlug: template.slug,
			variant: 'default',
			state: 'default',
			blockParentType: 'templates',
		});

		// Set those values to the current block in the fullRawTheme
		decodedTheme.templates[template.slug] = {
			...templateValue,
		};
	});

	return decodedTheme;
};

/**
 * Get decoded object for each property of an object
 * @param [object]
 *     object    => the current object of css properties
 *     rawTheme  => the raw theme
 *     blockSlug => the current block slug
 *     variant   => the current variant
 *     state     => the current state
 *     parentProperties => Array of depth properties (for example: 'image.size.margin' would be ['image', 'size', 'margin'])
 * 		 blockParentType => the type of block parent ('blocks' || 'templates')
 * @returns [object] the current value in theme.json or the default value from the attributes
 */
export const getDecodedObject = ({
	object,
	rawTheme,
	blockSlug,
	variant,
	state,
	parentProperties = [],
	blockParentType = 'blocks',
}) => {
	return Object.keys(object).reduce((acc, property) => {
		const properties = [...parentProperties, property];
		let currentValue;

		if (
			isObject(object[property]) &&
			!isBoxFluid(object[property]) &&
			!isColumns(object[property])
		) {
			// Continue for subObject (recursively)
			currentValue = getDecodedObject({
				object: object[property],
				rawTheme,
				blockSlug,
				variant,
				state,
				parentProperties: properties,
				blockParentType,
			});
		} else {
			// Default variant "value"
			let defaultVariantValue = getDefaultVariantValue({
				properties,
				blockSlug,
				rawTheme,
				blockParentType,
			});

			// Default variant "default"
			const defaultVariantDefault = getDefaultVariantDefault({
				attributeValue: object[property],
				rawTheme,
			});

			if (!variant || variant === 'default') {
				// Default variant
				if (!state || state === 'default') {
					// Default state

					// Inherits from attributes
					currentValue = mergeValues(
						defaultVariantValue,
						defaultVariantDefault
					);
				} else {
					// Any other state
					// => Doesn't inherit from default variant value/default if it doesn't have attributes defined as we only want to generate new css for the states if there's some specific value or attribute

					// Default variant state "value"
					const currentStateValue = getDefaultVariantStateValue({
						properties,
						blockSlug,
						rawTheme,
						state,
						blockParentType,
					});

					// Inherits from default variant value (only if has attributes defined) or attributes (Note: current State attributes were previously merged with default attributes)
					if (currentStateValue || defaultVariantDefault)
						currentValue = mergeValues(
							currentStateValue,
							defaultVariantValue,
							defaultVariantDefault
						);
				}
			} else {
				// Any other variant

				// Current variant "value"
				const currentVariantValue = getCurrentVariantValue({
					properties,
					blockSlug,
					rawTheme,
					variant,
					blockParentType,
				});

				// Current variant "default"
				const currentVariantDefault = getCurrentVariantDefault({
					attributeValue: object[property],
					defaultVariantValue,
					defaultVariantDefault,
					variant,
					rawTheme,
				});

				if (!state || state === 'default') {
					// Default state

					// Inherits from default variant value or attributes
					currentValue = mergeValues(
						currentVariantValue,
						currentVariantDefault
					);
				} else {
					// Any other state
					// => Doesn't inherit from variant value/default if it doesn't have attributes defined as we only want to generate new css for the states if there's some specific value or attribute

					// Current variant state value
					const currentStateValue = getCurrentVariantStateValue({
						properties,
						blockSlug,
						rawTheme,
						variant,
						state,
						blockParentType,
					});

					// Inherits from current variant value (only if has attributes defined) or attributes (Note: current State attributes were previously merged with default attributes)
					if (currentStateValue || defaultVariantDefault)
						currentValue = mergeValues(
							currentStateValue,
							currentVariantValue,
							currentVariantDefault
						);
				}
			}
		}

		// Decode value to replace any keypath by its actual value
		const decodedCurrentValue = getDecodedValue(currentValue, rawTheme);

		// Assign it to the current property only if there is a value (to avoid any unnecessary css for css-in-js)
		if (decodedCurrentValue) acc[property] = decodedCurrentValue;

		return acc;
	}, {});
};

/**
 * Get the full raw theme that contains the default values + the origin of them to know where a block is inheriting from
 * @param rawTheme [object] The raw theme
 * @param blocksThemeControls [array] The array of blocks theme controls (taken from theming-loader.ts)
 * @param templatesThemeControls [array] The array of templates theme controls (taken from theming-loader.ts)
 * @returns [object] fullRawTheme
 */
export const getFullRawTheme = (
	rawTheme,
	blocksThemeControls,
	templatesThemeControls
) => {
	if (
		(!blocksThemeControls || !blocksThemeControls.length) &&
		(!templatesThemeControls || !templatesThemeControls.length)
	)
		return rawTheme;

	// Deepclone
	const blocks = cloneDeep(blocksThemeControls);
	const templates = cloneDeep(templatesThemeControls);
	const fullTheme = { tokens: {}, blocks: {}, templates: {} };
	fullTheme.tokens = cloneDeep(rawTheme.tokens);

	blocks.map((block) => {
		// Loop throught each variant
		const { default: blockDefault, ...blockVariants } = Object.keys(
			rawTheme.tokens.variants
		).reduce(
			(acc, variant) => {
				// Get the values (default, origin, value) for each property
				const variantValue = getFullObject({
					object: omit(block.attributes, ['states']),
					rawTheme,
					blockSlug: block.slug,
					blockName: block.name,
					variant,
					state: 'default',
					blockParentType: 'blocks',
				});

				acc[variant] = variantValue;

				// Get the values for each states
				if (block.attributes['states']) {
					const statesValue = Object.keys(
						block.attributes['states']
					).reduce((acc, state) => {
						// Get the values (default, origin, value) for each property
						const stateValue = getFullObject({
							object: mergeDeep(
								omit(block.attributes, ['states']),
								block.attributes['states']?.[state]
							),
							rawTheme,
							blockSlug: block.slug,
							blockName: block.name,
							variant,
							state,
							blockParentType: 'blocks',
						});

						acc[state] = stateValue;

						return acc;
					}, {});

					acc[variant].states = statesValue;
				}

				return acc;
			},
			{ default: {} }
		);

		// Set those values to the current block in the fullRawTheme
		fullTheme.blocks[block.slug] = {
			...blockDefault,
			variants: { ...blockVariants },
		};
	});

	templates.map((template) => {
		// Get the values (default, origin, value) for each property
		const templateValue = getFullObject({
			object: template.attributes,
			rawTheme,
			blockSlug: template.slug,
			blockName: template.name,
			variant: 'default',
			state: 'default',
			blockParentType: 'templates',
		});

		// Set those values to the current block in the fullRawTheme
		fullTheme.templates[template.slug] = {
			...templateValue,
		};
	});

	return fullTheme;
};

/**
 * Get full object value (value, default, origin) for each property of an object
 * @param [object]
 *     object    => the current object of css properties
 *     rawTheme  => the raw theme
 *     blockSlug => the current block slug
 *     blockName => the current block name
 *     variant   => the current variant
 *     state     => the current state
 *     parentProperties => Array of depth properties (for example: 'image.size.margin' would be ['image', 'size', 'margin'])
 * 		 blockParentType => the type of block parent ('blocks' || 'templates')
 * @returns [object] the current value in theme.json + the default value + the default origin where it inherits from
 */
export const getFullObject = ({
	object,
	rawTheme,
	blockSlug,
	blockName,
	variant,
	state,
	parentProperties = [],
	blockParentType = 'blocks',
}) => {
	return Object.keys(object).reduce((acc, property) => {
		const properties = [...parentProperties, property];
		let currentValue;

		if (
			isObject(object[property]) &&
			!isBoxFluid(object[property]) &&
			!isColumns(object[property])
		) {
			// Continue for subObject (recursively)
			currentValue = getFullObject({
				object: object[property],
				rawTheme,
				blockSlug,
				blockName,
				variant,
				state,
				parentProperties: properties,
				blockParentType,
			});
		} else {
			// Default variant "value"
			const defaultVariantValue = getDefaultVariantValue({
				properties,
				rawTheme,
				blockSlug,
				blockParentType,
			});

			// Default variant "default"
			const defaultVariantDefault = getDefaultVariantDefault({
				attributeValue: object[property],
				rawTheme,
			});

			// Default variant "default origin"
			const defaultVariantDefaultOrigin = getDefaultVariantDefaultOrigin({
				attributeValue: object[property],
				rawTheme,
			});

			if (!variant || variant === 'default') {
				// Default variant
				if (!state || state === 'default') {
					// Default state

					// Inherits from attributes
					currentValue = {
						value: defaultVariantValue,
						default: defaultVariantDefault,
						default_origin: defaultVariantDefaultOrigin,
					};
				} else {
					// Any other state

					// Default variant state "value"
					const currentStateValue = getDefaultVariantStateValue({
						properties,
						blockSlug,
						rawTheme,
						state,
						blockParentType,
					});

					// Inherits from default variant value or attributes (Note: current State attributes were previously merged with default attributes)
					currentValue = {
						value: currentStateValue,
						default: mergeValues(
							defaultVariantValue,
							defaultVariantDefault
						),
						default_origin: defaultVariantValue
							? blockName
							: defaultVariantDefaultOrigin,
					};
				}
			} else {
				// Any other variant

				// Current variant "value"
				const currentVariantValue = getCurrentVariantValue({
					properties,
					blockSlug,
					rawTheme,
					variant,
					blockParentType,
				});

				// Current variant "default"
				const currentVariantDefault = getCurrentVariantDefault({
					attributeValue: object[property],
					defaultVariantValue,
					defaultVariantDefault,
					variant,
					rawTheme,
				});

				// Current variant "default origin"
				const currentVariantDefaultOrigin =
					getCurrentVariantDefaultOrigin({
						attributeValue: object[property],
						defaultVariantValue,
						defaultVariantDefaultOrigin,
						blockName,
						variant,
						state,
						rawTheme,
					});

				if (!state || state === 'default') {
					// Default state

					// Inherits from default variant value or attributes
					// (Note: If states => current State attributes were previously merged with default attributes)
					currentValue = {
						value: currentVariantValue,
						default: currentVariantDefault,
						default_origin: currentVariantDefaultOrigin,
					};
				} else {
					// Any other state

					// Current variant state value
					const currentStateValue = getCurrentVariantStateValue({
						properties,
						blockSlug,
						rawTheme,
						variant,
						state,
						blockParentType,
					});

					// Inherits from default variant value or attributes (Note: current State attributes were previously merged with default attributes)
					currentValue = {
						value: currentStateValue,
						default: mergeValues(
							currentVariantValue,
							currentVariantDefault
						),
						default_origin: currentVariantValue
							? `${blockName} - ${rawTheme.tokens.variants[variant].name} variant`
							: currentVariantDefaultOrigin,
					};
				}
			}
		}

		acc[property] = currentValue;

		return acc;
	}, {});
};

/**
 * Get the value from rawTheme for a specific property of the default variant
 * @param [object]
 *     properties => array of depth properties (for example: 'image.size.margin' would be ['image', 'size', 'margin'])
 *     blockSlug  => current block slug
 *     rawTheme   => raw theme
 * 		 blockParentType => the type of block parent ('blocks' || 'templates')
 */
const getDefaultVariantValue = ({
	properties,
	blockSlug,
	rawTheme,
	blockParentType,
}) => {
	return getValueFromProperties(
		properties,
		rawTheme[blockParentType][blockSlug]
	);
};

/**
 * Get the default value from a specific attribute value of the default variant
 * @param [object]
 *     attributeValue => Current attribute value
 *     rawTheme       => raw theme
 */
const getDefaultVariantDefault = ({ attributeValue, rawTheme }) => {
	// If current attribute value is a keypath (not from a typography which needs the actual keypath as the value) then return the value taken from the keypath itself
	// if (hasKeypathFromVariant(attributeValue)) {
	if (
		hasKeypathFromTokens(attributeValue) &&
		!hasKeypathFromTypography(attributeValue)
	) {
		return getValueFromKeypath(attributeValue, rawTheme);
	} else {
		return attributeValue;
	}
};

/**
 * Get the default origin from a specific attribute value of the default variant
 * @param [object]
 *     attributeValue => current attribute value
 *     rawTheme       => raw theme
 */
const getDefaultVariantDefaultOrigin = ({ attributeValue, rawTheme }) => {
	// If current attribute value is a keypath then the origin is the keypath token itself
	if (
		hasKeypathFromTokens(attributeValue) &&
		!hasKeypathFromTypography(attributeValue)
	) {
		const splittedKeypath = attributeValue.split('.');
		return `${
			rawTheme.tokens[splittedKeypath[1]][splittedKeypath[2]]?.name
		} ${splittedKeypath[1]} token`;
	} else {
		return ''; // No origin specified if it comes from the attributes
	}
};

/**
 * Get the value from rawTheme for a specific property of the default variant for a specific state
 * @param [object]
 *     properties => array of depth properties (for example: 'image.size.margin' would be ['image', 'size', 'margin'])
 *     blockSlug  => current block slug
 *     rawTheme   => raw theme
 *     state      => current state
 * 		 blockParentType => the type of block parent ('blocks' || 'templates')
 */
const getDefaultVariantStateValue = ({
	properties,
	blockSlug,
	rawTheme,
	state,
	blockParentType = 'blocks',
}) => {
	return getValueFromProperties(
		properties,
		rawTheme[blockParentType][blockSlug]?.states?.[state]
	);
};

/**
 * Get the value from rawTheme for a specific property of a specific variant
 * @param [object]
 *     properties => array of depth properties (for example: 'image.size.margin' would be ['image', 'size', 'margin'])
 *     blockSlug  => current block slug
 *     rawTheme   => raw theme
 *     variant    => current variant
 * 		 blockParentType => the type of block parent ('blocks' || 'templates')
 */
const getCurrentVariantValue = ({
	properties,
	blockSlug,
	rawTheme,
	variant,
	blockParentType = 'blocks',
}) => {
	return getValueFromProperties(
		properties,
		rawTheme[blockParentType][blockSlug]?.variants?.[variant]
	);
};

/**
 * Get the default value of a specific variant for the current property
 * @param [object]
 *     attributeValue        => current attribute value
 *     defaultVariantValue 	 => default variant "value"
 *     defaultVariantDefault => default variant "default value"
 *     variant    					 => current variant
 *     rawTheme       			 => raw theme
 */
const getCurrentVariantDefault = ({
	attributeValue,
	defaultVariantValue,
	defaultVariantDefault,
	variant,
	rawTheme,
}) => {
	if (
		attributeValue &&
		hasKeypathFromTokens(attributeValue) &&
		!hasKeypathFromTypography(attributeValue)
	) {
		// If current attribute value is a token keypath - but NOT from the typographies tokens which need the actual keypath as the value
		if (hasKeypathFromVariant(attributeValue)) {
			// If current attribute value is a variant keypath
			return getValueFromKeypath(
				attributeValue.replace('default', variant),
				rawTheme
			);
		} else {
			return getValueFromKeypath(attributeValue, rawTheme);
		}
	} else if (defaultVariantValue) {
		return mergeValues(defaultVariantValue, defaultVariantDefault);
	} else {
		return defaultVariantDefault;
	}
};

/**
 * Get the default origin of a specific variant for the current property
 * @param [object]
 *     attributeValue        			 => current attribute value
 *     defaultVariantValue 	  		 => default variant "value"
 *     defaultVariantDefaultOrigin => default variant "default origin"
 *     blockName  								 => current block name
 *     variant    					       => current variant
 *     state      					 			 => current state
 *     rawTheme       			 			 => raw theme
 */
const getCurrentVariantDefaultOrigin = ({
	attributeValue,
	defaultVariantValue,
	defaultVariantDefaultOrigin,
	blockName,
	variant,
	state,
	rawTheme,
}) => {
	if (
		attributeValue &&
		hasKeypathFromTokens(attributeValue) &&
		!hasKeypathFromTypography(attributeValue)
	) {
		// If current attribute value is a token keypath
		const splittedKeypath = attributeValue.split('.');
		return `${
			rawTheme.tokens[splittedKeypath[1]][variant ?? splittedKeypath[2]]
				?.name
		} ${splittedKeypath[1]} token`;
	} else if (defaultVariantValue && (!state || state === 'default')) {
		// Not for a state => if it's a state, it doesn't inherit from the default variant value
		return blockName;
	} else {
		return defaultVariantDefaultOrigin;
	}
};

/**
 * Get the value from rawTheme for a specific property of a specific variant for a specific state
 * @param [object]
 *     properties => array of depth properties (for example: 'image.size.margin' would be ['image', 'size', 'margin'])
 *     blockSlug  => current block slug
 *     rawTheme   => raw theme
 *     variant    => current variant
 *     state      => current state
 * 		 blockParentType => the type of block parent ('blocks' || 'templates')
 */
const getCurrentVariantStateValue = ({
	properties,
	blockSlug,
	rawTheme,
	variant,
	state,
	blockParentType = 'blocks',
}) => {
	if (state && state !== 'default') {
		// Any other state other then default
		return getValueFromProperties(
			properties,
			rawTheme[blockParentType][blockSlug]?.variants?.[variant]?.states?.[
				state
			]
		);
	} else {
		return null;
	}
};

/**
 * Recursive function that replaces keypath with value for each object depth
 * @param obj The object to decode
 * @param theme The reference theme to decode the keypath from
 */
export const decodeObject = (obj, theme) => {
	for (const prop in obj) {
		if (isObject(obj[prop])) {
			decodeObject(obj[prop], theme);
		} else {
			obj[prop] = getDecodedValue(obj[prop], theme) || '';
		}
	}
};

/**
 * Replaces the keypath with the corresponding value from the theme for each given value (if needed)
 * @param value
 * @param theme
 * @returns The decoded value (not a keypath anymore)
 */
const getDecodedValue = (value, theme) => {
	if (typeof value === 'string' && value.charAt(0) === '@') {
		// It's a keypath => replace keypath value by the actual value
		// It's changing each references of the same key/prop as it's not a deep clone
		let decodedValue = getValueFromKeypath(value, theme);

		// If the new value is an object, format this new object to replace new keypath if needed
		if (isObject(decodedValue)) {
			decodedValue = cloneDeep(decodedValue); // Use cloneDeep to avoid updating the rawTheme as well
			decodeObject(decodedValue, theme);
		}

		return decodedValue;
	} else {
		return value;
	}
};

// Check if current variable is a BoxFluid attribute or not
// It means it contains "top" or "right" or "bottom" or "left" and these values are Arrays (for SizeFluidControl)
// @returns [boolean]
const isBoxFluid = (variable) => {
	return (
		// default box
		(!!variable.top && Array.isArray(variable.top)) ||
		(!!variable.right && Array.isArray(variable.right)) ||
		(!!variable.bottom && Array.isArray(variable.bottom)) ||
		(!!variable.left && Array.isArray(variable.left)) ||
		// radius
		(!!variable.topLeft && Array.isArray(variable.topLeft)) ||
		(!!variable.topRight && Array.isArray(variable.topRight)) ||
		(!!variable.bottomRight && Array.isArray(variable.bottomRight)) ||
		(!!variable.bottomLeft && Array.isArray(variable.bottomLeft))
	);
};

// Check if current variable is a Columns attribute or not
// It means it contains "desktop" / "tablet" / "mobile" and those are objects (and not number or string)
// @returns [boolean]
const isColumns = (variable) => {
	return (
		(!!variable.desktop && typeof variable.desktop === 'object') ||
		(!!variable.tablet && typeof variable.tablet === 'object') ||
		(!!variable.mobile && typeof variable.mobile === 'object')
	);
};

// Check if current value is a keypath from a token
const hasKeypathFromTokens = (value) => {
	return typeof value === 'string' && value.startsWith('@tokens.');
};

// Check if current value is a keypath of a variant tokens
const hasKeypathFromVariant = (value) => {
	return (
		typeof value === 'string' &&
		value.startsWith('@tokens.variants.default')
	);
};

// Check if current value is a keypath of a typography tokens
const hasKeypathFromTypography = (value) => {
	return (
		typeof value === 'string' && value.startsWith('@tokens.typographies.')
	);
};

// Clears all values of an object (recursive function)
const clearObject = (object) => {
	for (const prop in object) {
		if (isObject(object[prop])) clearObject(object[prop]);
		else object[prop] = null;
	}
};

/**
 * Merge value with 1 or multiple attributes to avoid having `null` values
 * @param value
 * @param attributes (can be multiple attributes, seperated with commas)
 * @returns merged values
 */
const mergeValues = (value, ...attributes) => {
	if (!attributes || !attributes.length) return value;

	const newValue = mergeValueAndAttributes({
		value,
		attributes: attributes.shift(),
	});

	return mergeValues(newValue, ...attributes);
};

// Merge 1 value with 1 attribute depending on value type
const mergeValueAndAttributes = ({ value, attributes }) => {
	if (value) {
		if (isBoxFluid(value)) {
			return mergeBoxFluidValueAndAttributes({ value, attributes });
		} else if (isColumns(value)) {
			return mergeColumnsValueAndAttributes({ value, attributes });
		} else if (Array.isArray(value)) {
			return mergeFluidValueAndAttributes({ value, attributes });
		} else {
			return value;
		}
	} else {
		return attributes;
	}
};

// Merge value and attributes for fluidValues (arrays like [min, max])
const mergeFluidValueAndAttributes = ({ value, attributes }) => {
	const result = [
		value?.[0] != null // not null or undefined
			? value?.[0]
			: attributes?.[0] != null // not null or undefined
			? attributes?.[0]
			: null,
		value?.[1] != null // not null or undefined
			? value?.[1]
			: attributes?.[1] != null // not null or undefined
			? attributes?.[1]
			: null,
	];

	return result;
};

// Merge value and attributes for boxFluidValues (object of arrays like {top: [min, max], left: [min,max]})
const mergeBoxFluidValueAndAttributes = ({ value, attributes }) => {
	return Object.keys(attributes).reduce((acc, item) => {
		if (value[item]) {
			acc[item] = mergeFluidValueAndAttributes({
				value: value[item],
				attributes: attributes[item],
			});
		} else acc[item] = attributes[item];

		return acc;
	}, {});
};

// Merge value and attributes for columnsValue (object like {desktop: {offsetLeft: int, columnLeft: int, gapBetween: int, columnRight: int}})
const mergeColumnsValueAndAttributes = ({ value, attributes }) => {
	return mergeDeep(attributes, value);
};
